import { Button } from '@ordr-as/umami-react/button';
import React, { useState } from 'react';
import type { PaymentMethodDto } from '../../../../../../generated-api';
import { getInitalStateForDynamicUnintegratedPaymentFields } from '../../../../../utils/payment';
import { useScreenSize } from '../../../../../context/ScreenSizeContext';
import { useIntl } from 'react-intl';
import { useLocation } from '../../../../../context/LocationContext';
import { useCustomerRegistry, useCustomerRegistryEnabled } from '../../../../../hooks/useCustomerRegistry';
import { useDebounce } from 'use-debounce';
import CheckoutUnintegratedPaymentFields from './CheckoutUnintegratedPaymentFields';
import BackButton from '../../../../../components/BackButton';
import FormattedMessage from '../../../../../components/FormattedMessage';
import CloseIcon from '@ordr-as/icons/CloseIcon';
import TipField from '../../../../../components/TipField';
import PrintInvoiceDeliveryReceipt from '../PrintInvoiceDeliveryReceipt';

interface Props {
  cartTotalPrice: number;
  paymentMethod: PaymentMethodDto;
  onNavigateBack: () => void;
  onSubmit: (dynamicFields: Record<string, string>, tipAmount?: number) => void;
  isOpen: boolean;
}

const CheckoutUnintegratedPayment = (props: Props) => {
  const { cartTotalPrice, paymentMethod, onNavigateBack, onSubmit } = props;
  const initalStateForDynamicFields = getInitalStateForDynamicUnintegratedPaymentFields(paymentMethod);
  const [dynamicFieldValues, setDynamicFieldValues] = useState(initalStateForDynamicFields);
  const dynamicFields = paymentMethod.unintegratedPaymentFields;
  const requiredFields = dynamicFields?.filter((field) => field.required);
  const { isPhone } = useScreenSize();
  const { formatMessage } = useIntl();
  const { location } = useLocation();
  const { data: customerRegistryEnabledDto } = useCustomerRegistryEnabled(location.id);
  const [searchText, setSearchText] = useDebounce<string | undefined>(undefined, 300);
  const { data: customerRegistry } = useCustomerRegistry(location.id, searchText);
  const [isTipAmountValid, setIsTipAmountValid] = useState(true);
  const [tipAmount, setTipAmount] = useState<number | undefined>(undefined);

  const canSubmit =
    !requiredFields?.map((requiredField) => dynamicFieldValues[requiredField.name]).includes('') && isTipAmountValid;
  const isInvoicePayment = paymentMethod.provider === 'UNINTEGRATED' && paymentMethod.paymentMethod === 'INVOICE';

  const handleCompletePurchase = () => {
    if (canSubmit) {
      onSubmit(dynamicFieldValues, tipAmount);
      onNavigateBack();
    }
  };

  return (
    <>
      {!isPhone && <BackButton navigateTo={onNavigateBack} />}
      <div className="p-4 tablet:p-6 h-full mb-[96px]">
        <div className="flex flex-row w-full pb-4">
          <h3 className="basis-5/6 mb-2">
            {paymentMethod.label ??
              formatMessage({ id: 'payment.paymentMethods.unintegratedFieldsDialog.title.fallback' })}
          </h3>
          {isPhone && (
            <Button className="basis-1/6" onClick={onNavigateBack}>
              <CloseIcon />
            </Button>
          )}
        </div>
        <CheckoutUnintegratedPaymentFields
          paymentMethod={paymentMethod}
          dynamicFields={dynamicFields}
          autoFocus={false}
          dynamicFieldValues={dynamicFieldValues}
          setDynamicFieldValues={setDynamicFieldValues}
          setSearchText={setSearchText}
          customerRegistryEnabledDto={customerRegistryEnabledDto}
          customerRegistry={customerRegistry}
        />
        {isInvoicePayment && (
          <>
            <TipField
              amountToPay={cartTotalPrice}
              setTipAmount={setTipAmount}
              setIsTipAmountValid={setIsTipAmountValid}
            />
            <PrintInvoiceDeliveryReceipt
              customerName={customerRegistryEnabledDto?.enabled ? undefined : dynamicFieldValues['externalReference']}
              referencePerson={dynamicFieldValues['referencePerson']}
            />
          </>
        )}
      </div>
      <div className="fixed w-full tablet:w-[33%] bottom-0 bg-brand-white shadow-[15px_-15px_50px_-19px_rgb(0,0,0,15%)] py-4 px-4 tablet:px-6">
        <Button onClick={handleCompletePurchase} fullWidth kind="primary" isDisabled={!canSubmit}>
          <FormattedMessage id="generic.confirm" />
        </Button>
      </div>
    </>
  );
};

export default CheckoutUnintegratedPayment;
